import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis } from 'recharts';

import { ReactComponent as NavigateArrow } from '../../../assets/icons/dashboard/navigate-arrow.svg';
import { chartTimePeriod } from '../../../config/enum';
import currentTimeZone from '../../../config/timeZone';
import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
import dashboard from '../../../utils/api/dashboard';

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

/**
 * Device duration - idle and in-use comparison chart
 * @returns
 */
export default function DeviceDurationChart() {
  const navigate = useNavigate();
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);

  /**
   * Fetch power consumed data based on time range
   * @param range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    try {
      const response = await dashboard.getDeviceActiveIdleReport({
        timePeriod: range,
        timeZone: currentTimeZone,
      });

      setBarGraphData(response?.data);
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Function to show custom tooltip
   * @param param0
   * @returns
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0];

      return (
        <div className="device-duration-custom-tooltip">
          <p className="label">{data?.payload?.label}</p>
          <div className="tooltip-values">
            <p style={{ color: '#2AA700' }}>In use: {data?.payload?.activeHours || 0}</p>
            <p style={{ color: '#0099D6' }}>Idle: {data?.payload?.idleHours}</p>
          </div>
        </div>
      );
    }

    return null;
  }

  useEffect(() => {
    fetchPowerConsumedReport('last12months');
  }, []);

  return (
    <div className="dashboard-device-duration-chart-wrap">
      <div className="dashboard-device-duration-chart-header">
        <div className="header-title">
          <h6>Duration ( Device )</h6>
          <span
            className="header-arrow"
            role="presentation"
            onClick={() => navigate('/ev-admin/device-management')}>
            <NavigateArrow />
          </span>
        </div>
        <CustomToggleButton
          labels={chartTimePeriod.map((duration) => duration.label)}
          onToggle={(index, label) =>
            fetchPowerConsumedReport(
              chartTimePeriod.find((duration) => duration.label === label)?.value || 'last12months',
            )
          }
          defaultActiveIndex={0}
          type="chart"
        />
      </div>
      <div className="dashboard-device-duration-chart-container">
        <ResponsiveContainer width="100%" height={275}>
          <BarChart data={barGraphData} barCategoryGap={25}>
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              fontSize={12}
              stroke="#535353"
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
            {/* <YAxis
                      axisLine={false}
                      tickLine={false}
                      domain={['dataMin - 1', 'dataMax + 1']}
                    /> */}
            <Bar dataKey="activeHours" stackId="a" barSize={26} fill="#2AA700" />
            <Bar dataKey="idleHours" stackId="a" barSize={26} fill="#0099D6" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="device-duration-chart-label">
        <div className="each-label">
          <div className="each-label-color" style={{ backgroundColor: '#2AA700' }} />
          <p>In use</p>
        </div>
        <div className="each-label">
          <div className="each-label-color" style={{ backgroundColor: '#0099D6' }} />
          <p>Idle</p>
        </div>
      </div>
    </div>
  );
}
